/**
 * Real Vendor API
 *
 * Connects to actual backend endpoints for vendor management.
 */

import type {
  ApiResponse,
  PaginatedResponse,
  Vendor,
  VendorListItem,
  VendorOnboardingData,
  VendorApprovalAction,
  VendorStats,
  VendorSignupData,
} from '../types'
import { http } from './httpClient'

/**
 * Vendor API
 */
export const realVendorApi = {
  // ============================================================================
  // Vendor Signup & Onboarding
  // ============================================================================

  /**
   * Register as a vendor
   */
  async signup(data: VendorSignupData): Promise<ApiResponse<{ vendorId: string; userId: string }>> {
    return http.post('/vendors/signup', data)
  },

  /**
   * Get vendor onboarding data
   */
  async getOnboarding(vendorId: string): Promise<ApiResponse<Vendor>> {
    return http.get(`/vendors/${vendorId}/onboarding`)
  },

  /**
   * Submit onboarding step 1
   */
  async submitStep1(vendorId: string, data: FormData): Promise<ApiResponse<{ step: number }>> {
    return http.upload(`/vendors/${vendorId}/onboarding/step1`, data)
  },

  /**
   * Submit onboarding step 2
   */
  async submitStep2(
    vendorId: string,
    data: VendorOnboardingData['step2']
  ): Promise<ApiResponse<{ step: number }>> {
    return http.post(`/vendors/${vendorId}/onboarding/step2`, data)
  },

  /**
   * Submit onboarding step 3 (final)
   */
  async submitStep3(
    vendorId: string,
    data: VendorOnboardingData['step3']
  ): Promise<ApiResponse<Vendor>> {
    return http.post(`/vendors/${vendorId}/onboarding/step3`, data)
  },

  /**
   * Complete onboarding (submit for approval)
   */
  async completeOnboarding(vendorId: string): Promise<ApiResponse<Vendor>> {
    return http.post(`/vendors/${vendorId}/onboarding/complete`)
  },

  // ============================================================================
  // Vendor Profile & Account
  // ============================================================================

  /**
   * Get vendor by ID
   */
  async getVendor(vendorId: string): Promise<ApiResponse<Vendor>> {
    return http.get(`/vendors/${vendorId}`)
  },

  /**
   * Update vendor profile
   */
  async updateProfile(vendorId: string, data: Partial<Vendor>): Promise<ApiResponse<Vendor>> {
    return http.put(`/vendors/${vendorId}`, data)
  },

  /**
   * Upload vendor logo
   */
  async uploadLogo(vendorId: string, file: File): Promise<ApiResponse<{ logoUrl: string }>> {
    const formData = new FormData()
    formData.append('logo', file)
    return http.upload(`/vendors/${vendorId}/logo`, formData)
  },

  /**
   * Upload vendor document
   */
  async uploadDocument(
    vendorId: string,
    file: File,
    type: string
  ): Promise<ApiResponse<{ documentId: string; url: string }>> {
    const formData = new FormData()
    formData.append('document', file)
    formData.append('type', type)
    return http.upload(`/vendors/${vendorId}/documents`, formData)
  },

  /**
   * Delete vendor document
   */
  async deleteDocument(vendorId: string, documentId: string): Promise<ApiResponse<void>> {
    return http.delete(`/vendors/${vendorId}/documents/${documentId}`)
  },

  // ============================================================================
  // Stripe Connect
  // ============================================================================

  /**
   * Get Stripe Connect onboarding link
   */
  async getStripeConnectLink(vendorId: string): Promise<ApiResponse<{ url: string }>> {
    return http.get(`/vendors/${vendorId}/stripe/connect`)
  },

  /**
   * Get Stripe Connect status
   */
  async getStripeStatus(
    vendorId: string
  ): Promise<ApiResponse<{ connected: boolean; accountId?: string }>> {
    return http.get(`/vendors/${vendorId}/stripe/status`)
  },

  // ============================================================================
  // Agreement
  // ============================================================================

  /**
   * Generate vendor agreement
   */
  async generateAgreement(vendorId: string): Promise<ApiResponse<{ agreementUrl: string }>> {
    return http.post(`/vendors/${vendorId}/agreement/generate`)
  },

  /**
   * Sign vendor agreement
   */
  async signAgreement(vendorId: string, signature: string): Promise<ApiResponse<Vendor>> {
    return http.post(`/vendors/${vendorId}/agreement/sign`, { signature })
  },

  /**
   * Download signed agreement
   */
  async downloadAgreement(vendorId: string): Promise<ApiResponse<{ downloadUrl: string }>> {
    return http.get(`/vendors/${vendorId}/agreement/download`)
  },

  // ============================================================================
  // Admin: Vendor Management
  // ============================================================================

  /**
   * List all vendors (admin)
   */
  async listVendors(params?: {
    page?: number
    limit?: number
    status?: string
    search?: string
  }): Promise<PaginatedResponse<VendorListItem>> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.limit) queryParams.set('limit', params.limit.toString())
    if (params?.status) queryParams.set('status', params.status)
    if (params?.search) queryParams.set('search', params.search)

    return http.getPaginated(`/admin/vendors?${queryParams.toString()}`)
  },

  /**
   * Get vendor statistics (admin)
   */
  async getStats(): Promise<ApiResponse<VendorStats>> {
    return http.get('/admin/vendors/stats')
  },

  /**
   * Approve, reject, or request modification (admin)
   */
  async updateApproval(action: VendorApprovalAction): Promise<ApiResponse<Vendor>> {
    return http.post(`/admin/vendors/${action.vendorId}/approval`, {
      action: action.action,
      reason: action.reason,
    })
  },

  /**
   * Suspend vendor (admin)
   */
  async suspendVendor(vendorId: string, reason: string): Promise<ApiResponse<Vendor>> {
    return http.post(`/admin/vendors/${vendorId}/suspend`, { reason })
  },

  /**
   * Reactivate vendor (admin)
   */
  async reactivateVendor(vendorId: string): Promise<ApiResponse<Vendor>> {
    return http.post(`/admin/vendors/${vendorId}/reactivate`)
  },

  // ============================================================================
  // Vendor Orders, Analytics, Events (stubs — no backend yet)
  // ============================================================================

  async getOrders(_vendorId: string): Promise<ApiResponse<unknown[]>> {
    console.warn('[realVendorApi] getOrders is stubbed — no backend route')
    return { success: true, data: [] }
  },

  async updateOrderStatus(orderId: string, status: string): Promise<ApiResponse<unknown>> {
    console.warn('[realVendorApi] updateOrderStatus is stubbed — no backend route')
    return { success: true, data: { id: orderId, status } }
  },

  async getAnalytics(_vendorId: string): Promise<ApiResponse<unknown>> {
    console.warn('[realVendorApi] getAnalytics is stubbed — no backend route')
    return { success: true, data: {} }
  },

  async getMonthlyData(_vendorId: string): Promise<ApiResponse<unknown[]>> {
    console.warn('[realVendorApi] getMonthlyData is stubbed — no backend route')
    return { success: true, data: [] }
  },

  async getTopChallenges(_vendorId: string): Promise<ApiResponse<unknown[]>> {
    console.warn('[realVendorApi] getTopChallenges is stubbed — no backend route')
    return { success: true, data: [] }
  },

  async getVendorEvents(_vendorId: string): Promise<ApiResponse<unknown[]>> {
    console.warn('[realVendorApi] getVendorEvents is stubbed — no backend route')
    return { success: true, data: [] }
  },

  async createEvent(_vendorId: string, _data: unknown): Promise<ApiResponse<unknown>> {
    console.warn('[realVendorApi] createEvent is stubbed — no backend route')
    return { success: true, data: {} }
  },

  async updateEvent(_eventId: string, _data: unknown): Promise<ApiResponse<unknown>> {
    console.warn('[realVendorApi] updateEvent is stubbed — no backend route')
    return { success: true, data: {} }
  },

  async deleteEvent(_eventId: string): Promise<ApiResponse<void>> {
    console.warn('[realVendorApi] deleteEvent is stubbed — no backend route')
    return { success: true, data: undefined as unknown as void }
  },
}
